/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import urlJoin from 'proper-url-join';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { AppDispatch, RootState } from '../../../../ts/state';
import * as messagesActions from '../../store/messages/actions';
import * as planActions from '../../store/plan/actions';
import { getAccountIdByEnvironment } from '../../util/account';
import { cloudConsoleUrl } from '../../util/config';
import { hasScheduler } from '../../util/contactModule';
import { parse as isCrn } from '../../util/crn';
import localStorage from '../../util/localStorage';
import { ctaEvents } from '../../util/trackers/segment';
import * as segment from '../../util/trackers/track-when-ready';
import { UpgradeFeature } from '../upgrade-button/UpgradeButton';
import EnterpriseUpgradeModal from '../upgrade-instance-modal/EnterpriseUpgradeModal';
import PlusConfirmationModal from '../upgrade-instance-modal/PlusConfirmationModal';
import PlusStepsModal from '../upgrade-instance-modal/PlusStepsModal';
import TrialConfirmationModal from '../upgrade-instance-modal/TrialConfirmationModal';
import TrialWelcomeModal from '../upgrade-instance-modal/TrialWelcomeModal';
import { UpgradeInstanceModal } from '../upgrade-instance-modal/UpgradeInstanceModal';

const ADD_PAYMENT = 'userAddPayment';
const UPGRADE_PLAN = 'planUpgradeRequest';

const strings = {
  UPGRADE_PLUS: 'Your instance is now using this plan: Plus',
  ACCOUNT_OWNER: (userid: string) => `The owner for this account is ${userid}.`
};

const modals = {
  TRIAL_CONFIRMATION: 'trialConfirmation',
  TRIAL_WELCOME: 'trialWelcome',
  PLUS_STEPS: 'plusSteps',
  PLUS_CONFIRMATION: 'plusConfirmation'
};

const URX_FORM =
  'https://www.ibm.com/account/reg/us-en/signup?formid=MAIL-watson';

export interface UpgradeModalProps {
  upgradingPlan: boolean;
  planName: string;
  showModal: boolean;
  featureName: string;
  noPlusTrial: boolean;
  instance: string;
  region: string;
  upgradeTo: string;
  user: any;
  accountType: string;
  planActions: typeof planActions;
  messagesActions: any;
  upgradingPlanName: string;
  upgradingPlanError?: { error: string; transactionId: string };
  accountOwner: string;
}

export class UpgradeModal extends React.Component<UpgradeModalProps> {
  state = {
    modalState: ''
  };

  componentDidMount() {
    const { user, instance } = this.props;
    if (user && instance) {
      if (isCrn(instance)) {
        // TODO: This is a work-around because subscriptions API in MCSP won't be available till Jan 24
        // So we are passing in instance id and mapping subscription info available in GET instance/ calls
        const accountId = getAccountIdByEnvironment(instance);
        this.props.planActions.getAccountType(accountId);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { upgradingPlan, planName, instance, user, accountType } = this.props;

    if (prevProps.upgradingPlan && !upgradingPlan) {
      this.planUpgradeComplete();
    }

    if (user && instance) {
      if (prevProps.user !== user || prevProps.instance !== instance) {
        if (isCrn(instance)) {
          // TODO: This is a work-around because subscriptions API in MCSP won't be available till Jan 24
          // So we are passing in instance id and mapping subscription info available in GET instance/ calls
          const accountId = getAccountIdByEnvironment(instance);
          this.props.planActions.getAccountType(accountId);
        }
      }
    }

    if (user && accountType) {
      if (prevProps.user !== user || prevProps.accountType !== accountType) {
        this.paymentAdditionCheck();
      }
    }

    if (user && planName) {
      this.planUpgradeCheck();
    }
  }

  render() {
    const {
      showModal,
      planName,
      upgradingPlan,
      featureName,
      instance,
      accountType,
      noPlusTrial,
      upgradeTo
    } = this.props;
    const { modalState } = this.state;

    if (!showModal || !planName) {
      return null;
    }

    if (showModal && modalState === modals.TRIAL_CONFIRMATION) {
      return (
        <TrialConfirmationModal
          upgradingPlan={upgradingPlan}
          onRequestClose={this.handleCloseClick}
          onSecondarySubmit={this.handleCancelClick}
          onRequestSubmit={() => this.handleConfirmClick('plus_trial')}
        />
      );
    }

    if (showModal && modalState === modals.PLUS_STEPS) {
      return (
        <PlusStepsModal
          onRequestClose={this.handleCloseClick}
          onRequestSubmit={this.handlePlusBeginClick}
        />
      );
    }

    if (showModal && modalState === modals.PLUS_CONFIRMATION) {
      return (
        <PlusConfirmationModal
          upgradingPlan={upgradingPlan}
          onRequestClose={this.handleCloseClick}
          onSecondarySubmit={this.handleCancelClick}
          onRequestSubmit={() => this.handleConfirmClick('plus')}
        />
      );
    }

    if (
      showModal &&
      (upgradeTo === 'enterprise' ||
        planName === 'plus' ||
        (planName !== 'lite' && featureName === UpgradeFeature.VERSIONS))
    ) {
      return (
        <EnterpriseUpgradeModal
          onConfirm={() => this.handleGetPricingClick('Enterprise column')}
          onClose={this.handleCloseClick}
        />
      );
    }

    if (showModal && modalState === modals.TRIAL_WELCOME) {
      return (
        <TrialWelcomeModal
          hasScheduler={hasScheduler()}
          onClose={this.handleCloseClick}
        />
      );
    }

    return (
      <UpgradeInstanceModal
        instance={instance}
        planName={planName}
        accountType={accountType}
        noTrial={noPlusTrial}
        handleCloseClick={this.handleCloseClick}
        handleUpgradeButtonClick={this.handleUpgradeButtonClick}
        handleGetPricingClick={this.handleGetPricingClick}
      />
    );
  }

  // Function: handleUpgradeButtonClick
  // Redirects user to correct click handler based on plan
  handleUpgradeButtonClick = () => {
    const { planName, noPlusTrial } = this.props;
    if (planName === 'lite' && !noPlusTrial) {
      this.handleTrialUpgradeButtonClick();
    } else if (
      planName === 'lite' ||
      planName === 'standard' ||
      planName === 'plus_trial'
    ) {
      this.handlePlusUpgradeButtonClick();
    }
  };

  // Function: handleTrialUpgradeButtonClick
  // Sets modal state to TrialConfirmationModal
  handleTrialUpgradeButtonClick = () => {
    ctaEvents.upgrade({
      category: 'Clicked Start Free Trial',
      uiElement: 'Start Free Trial Button',
      successFlag: true
    });
    this.setState({ modalState: modals.TRIAL_CONFIRMATION });
  };

  // Function: handlePlusUpgradeButtonClick
  // - Sets modal state to PlusConfirmationModal if payment info already exists
  // - Sets modal state to PlusStepsModal If payment info missing
  handlePlusUpgradeButtonClick = () => {
    const { accountType } = this.props;
    if (accountType) {
      // If paid account type ('PAYG'/'SUBSCRIPTION')
      if (accountType === 'PAYG' || accountType === 'SUBSCRIPTION') {
        ctaEvents.upgrade({
          category: 'Clicked Upgrade to save work button',
          uiElement: 'Upgrade button - Plus',
          successFlag: true,
          resultValue: 'CC info already exists, Plus Confirmation Modal Shown'
        });
        this.setState({ modalState: modals.PLUS_CONFIRMATION });
      }
      // Else non-paid account type ('FREE'/'TRIAL'/'STANDARD')
      else {
        ctaEvents.upgrade({
          category: 'Clicked Upgrade to save work button',
          uiElement: 'Upgrade Button - Plus',
          successFlag: true,
          resultValue: 'Need CC info, Plus Upgrade Modal Shown'
        });
        this.setState({ modalState: modals.PLUS_STEPS });
      }
    }
  };

  // Function: handleGetPricingClick
  // Handles get pricing button click on EnterpriseUpgradeModal and UpgradeInstanceModal
  handleGetPricingClick = whereFrom => {
    segment.customEvent(
      'Clicked Get pricing Button',
      whereFrom,
      'Button',
      true,
      'URX form opens'
    );
    this.props.planActions.closeUpgradeModal();
    this.setState({ modalState: '' });
    if (URX_FORM) {
      window.open(URX_FORM, '_blank');
    }
  };

  // Function: handleCloseClick
  // Handles 'Close' button click on PlusStepsModal, UpgradeInstanceModal, EnterpriseUpgradeModal, TrialConfirmationModal and PlusConfirmationModal
  handleCloseClick = () => {
    ctaEvents.upgradeModal({
      uielement: 'close button',
      category: 'close',
      successFlag: true
    });
    this.props.planActions.closeUpgradeModal();
    this.setState({ modalState: '' });
  };

  // Function: handleCancelClick
  // Handles 'Cancel' button click on TrialConfirmationModal and PlusConfirmationModal
  handleCancelClick = () => {
    ctaEvents.upgradeModal({
      uiElement: 'cancel button',
      successFlag: true,
      resultValue: 'User does not upgrade account',
      category: 'upgrade confirmation modal'
    });
    this.setState({ modalState: '' });
  };

  // Function: handleConfirmClick
  // Handles 'Confirm' button click on TrialConfirmationModal and PlusConfirmationModal
  handleConfirmClick = upgradingPlanName => {
    const { region, instance } = this.props;
    ctaEvents.upgradeModal({
      uiElement: 'agree button',
      successFlag: true,
      resultValue: 'User upgrades account',
      category: 'upgrade confirmation modal'
    });
    this.props.planActions.upgradePlanStart(
      region,
      instance,
      upgradingPlanName
    );
  };

  // Function: handlePlusBeginClick
  // - Handles 'Begin' button click on PlusStepsModal
  // - Caches 'ADD_PAYMENT' in local storage before redirecting user to payment page
  handlePlusBeginClick = () => {
    const { region, instance } = this.props;
    let addPaymentUrl;

    ctaEvents.upgradeModal({
      uiElement: 'begin button',
      successFlag: true,
      resultValue: 'User leaves to add cc info',
      category: 'plus upgrade'
    });

    localStorage.set(ADD_PAYMENT, {
      region,
      instance,
      upgradingPlanName: 'plus'
    });
    if (cloudConsoleUrl) {
      addPaymentUrl = urlJoin(
        cloudConsoleUrl,
        '/services/conversation/',
        encodeURIComponent(instance),
        '?paneId=manage&paywall=true'
      );
      window.location.href = addPaymentUrl;
    }
  };

  // Function: paymentAdditionCheck
  // - Checks for local storage cached 'ADD_PAYMENT' from before user left to payment page
  // - Checks to see if user added payment info (if account is a paid type)
  // - If both exist, sets modal state to PlusConfirmationModal
  paymentAdditionCheck = () => {
    const { instance, region, accountType, planActions } = this.props;
    const paymentAdded = localStorage.getContext()
      ? localStorage.get(ADD_PAYMENT)
      : null;

    if (
      paymentAdded &&
      region === paymentAdded.region &&
      instance === paymentAdded.instance
    ) {
      localStorage.remove(ADD_PAYMENT);

      if (accountType === 'PAYG' || accountType === 'SUBSCRIPTION') {
        planActions.showUpgradeModal();
        this.setState({ modalState: modals.PLUS_CONFIRMATION });
      }
    }
  };

  // Function: planUpgradeComplete
  // - Handles actual upgrading of plan
  // - Caches 'UPGRADE_PLAN' in local storage before reloading the page
  planUpgradeComplete = () => {
    const {
      upgradingPlanName,
      upgradingPlanError,
      accountOwner,
      region,
      instance
    } = this.props;
    this.props.planActions.closeUpgradeModal();
    this.setState({ modalState: '' });
    if (upgradingPlanError) {
      let errorMessage = upgradingPlanError.error || upgradingPlanError;
      if (accountOwner) {
        errorMessage += '<br>' + strings.ACCOUNT_OWNER(accountOwner);
      }
      this.props.messagesActions.addMessage({
        message: errorMessage,
        status: 'error',
        transactionId: upgradingPlanError.transactionId
      });
    } else {
      localStorage.set(UPGRADE_PLAN, {
        region,
        instance,
        planName: upgradingPlanName
      });
      if (window.location.pathname.endsWith('/planLimit')) {
        window.location.href = window.location.pathname.replace(
          '/planLimit',
          '/home'
        );
      } else {
        window.location.reload();
      }
    }
  };

  // Function: planUpgradeCheck
  // - Checks for local storage cached 'UPGRADE_PLAN' from before page had reloaded
  // - Sends successful upgrade message
  planUpgradeCheck = () => {
    const { planName, planActions } = this.props;
    const upgrade = localStorage.getContext()
      ? localStorage.get(UPGRADE_PLAN)
      : null;
    if (upgrade && _.isMatch(this.props, upgrade)) {
      localStorage.remove(UPGRADE_PLAN);
      if (planName === 'plus_trial') {
        planActions.showUpgradeModal();
        this.setState({ modalState: modals.TRIAL_WELCOME });
      } else if (planName === 'plus') {
        this.props.messagesActions.addMessage({
          message: strings.UPGRADE_PLUS,
          status: 'success'
        });
      }
    }
  };
}

const mapStateToProps = (state: RootState) => ({
  showModal: state.plan.showModal,
  upgradingPlan: state.plan.upgradingPlan,
  upgradingPlanName: state.plan.upgradingPlanName,
  upgradingPlanError: state.plan.upgradingPlanError,
  featureName: state.plan.featureName,
  planName:
    (state.instance.plan && state.instance.plan.name) ||
    (state.session.instanceState && state.session.instanceState.plan),
  accountType: state.plan.accountType,
  accountOwner: state.session.account && state.session.account.owner_userid,
  noPlusTrial: state.plan.noPlusTrial,
  region: state.session.region,
  instance: state.session.instance,
  user: state.session.user && state.session.user.username,
  upgradeTo: state.plan.upgradeTo
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  planActions: bindActionCreators(planActions, dispatch),
  messagesActions: bindActionCreators(
    _.omit(messagesActions, 'ToastStatus'),
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(UpgradeModal);
